import React, { useEffect } from 'react'
import { ElementProps } from '../base/element'
import { EventType } from '../schemamanager/schemaManager'

import {
  ICommonProps,
  IComponent,
  IDefaultStyles,
  IStyles,
} from '../schemamanager/types'

export type IButtonKind = 'standard' | 'outline' | 'pill' | 'flat' | 'icon' | 'toolbar'

export interface IButtonComponent extends ICommonProps {
  type: 'button' | 'link'
  label?: string
  kind?: IButtonKind
  buttonType?: 'button' | 'submit' | 'reset'
  iconComp?: any
  styleLabel?: IStyles
  styleIcon?: IStyles
}

export const Button = (props: ElementProps) => {
  const { sm, events, themeColor, addProps } = props
  const comp = props.comp as IButtonComponent

  const ref: any = React.useRef()
  const kind: IButtonKind = comp.kind || 'standard'

  const doFocus = (compToFocus: IComponent) => {
    if (compToFocus === comp) {
      ref.current?.focus()
    }
  }

  useEffect(() => {
    sm.on(EventType.FocusComponent, doFocus)
    return () => {
      sm.removeListener(EventType.FocusComponent, doFocus)
    }
  }, [sm])

  const styleStandard: IDefaultStyles = {
    main: {
      classes: {
        margin: 'mx-2',
        padding: 'px-4 py-2',
        flex: 'inline-flex',
        // gap: 'col-gap-2',
        alignItems: 'items-center',
        alignContent: 'content-center',
        backgroundColor: themeColor().bgColor,
        borderColor: 'border-transparent',
        borderRadius: 'rounded',
        hover: {
          backgroundColor: themeColor().hoverBgColor
        },
        focus: {
          outline: 'focus:outline-none',
          boxShadow: 'focus:shadow-outline'
        }
      },
      class: 'disabled:opacity-50'
    },
    styleLabel: {
      classes: {
        textColor: 'text-white',
        fontWeight: 'font-bold'
      }
    },
    styleIcon: {
      classes: {
        height: 'h-6',
        width: 'w-6',
        textColor: 'text-white',
        fontWeight: 'font-bold'
      }
    }
  }

  const styleInverted: IDefaultStyles = {
    main: {
      classes: {
        ...styleStandard.main.classes,
        backgroundColor: 'bg-transparent',
        hover: {
          borderColor: 'hover:border-transparent',
          backgroundColor: themeColor().hoverBgColorLighter,
          textColor: 'hover:text-white'
        }
      },
      class: styleStandard.main.class
    },
    styleLabel: {
      classes: {
        textColor: themeColor().textColor,
        fontWeight: 'font-semibold'
      }
    },
    styleIcon: {
      classes: {
        height: 'h-6',
        width: 'w-6',
        textColor: themeColor().textColor,
        fontWeight: 'font-bold'
      }
    }
  }

  let style: IDefaultStyles = styleStandard

  if (kind === 'pill') {
    style.main.classes!.borderRadius = 'rounded-full'
  } else if (kind === 'outline') {
    style = styleInverted
    style.main.classes!.borderWidth = 'border'
    style.main.classes!.borderColor = themeColor().borderColor
  } else if (kind === 'flat') {
    style = styleInverted
  } else if (kind === 'icon') {
    style = styleInverted
    style.main.classes!.height = 'h-16'
    style.main.classes!.width = 'w-14'
    style.main.classes!.borderRadius = 'rounded-full'
    style.styleIcon.classes!.height = 'h-8'
    style.styleIcon.classes!.width = 'w-8'
  } else if (kind === 'toolbar') {
    style.main.classes!.margin = 'mx-1'
    style.main.classes!.padding = 'px-3 py-2'
    style.main.classes!.height = 'h-14'
    style.main.classes!.width = 'w-14'
    style.main.classes!.borderRadius = 'rounded-full'
    style.styleIcon.classes!.height = 'h-8'
    style.styleIcon.classes!.width = 'w-8'
  }

  const Comp = comp.type === 'link' ? 'a' : 'button'
  if (comp.type === 'link' && addProps().disabled) {
    style.main.classes!.opacity = 'opacity-50'
  }
  
  const label = comp.kind === 'icon' || comp.kind === 'toolbar' ? '' : comp.label
  if (label && comp.iconComp) {
    style.styleLabel.classes!.margin = 'ml-2'
  }

  const m = props.mergeStyles(style)
  

  return (
    <Comp
      ref={ref}
      {...m.main}
      {...events()}
      {...addProps()}
    >
      {comp.iconComp && (
        <comp.iconComp {...m.styleIcon} />
      )} 
      {label && (
        <span {...m.styleLabel}>{label}</span>
      )}
      {sm.Schema.tooltipComp && (
        <sm.Schema.tooltipComp />
      )} 
    </Comp>
  )
}
